import { Controller } from "@hotwired/stimulus"

// A browser test call with the voice agent (voice-server#62): starts a test conversation, joins its LiveKit room
// with the microphone and plays the agent back. The call ends when either side hangs up.
export default class extends Controller {
  static targets = ["start", "hangUp", "enableAudio", "status", "audio", "conversationLink"]
  static values = { url: String }

  disconnect() {
    this.endCall()
  }

  async start() {
    const abandoned = () => !this.element.isConnected // the page went away mid-start
    this.startTarget.disabled = true
    this.conversationLinkTarget.classList.add("hidden")
    this.setStatus("Connecting…")

    let room
    try {
      // The WebRTC client is loaded on demand: it's large, and only a test call needs it
      const [livekit, call] = await Promise.all([import("livekit-client"), this.startCall()])
      if (abandoned()) return
      this.hangUpUrl = call.hang_up_url
      if (call.conversation_url) {
        this.conversationLinkTarget.href = call.conversation_url
        this.conversationLinkTarget.classList.remove("hidden")
      }
      room = this.buildRoom(livekit)
      await room.connect(call.url, call.token)
      if (abandoned()) return room.disconnect()
      await room.localParticipant.setMicrophoneEnabled(true)
      if (abandoned()) return room.disconnect()
      this.room = room
      this.startTarget.classList.add("hidden")
      this.hangUpTarget.classList.remove("hidden")
      if (room.remoteParticipants.size === 0) this.setStatus("Waiting for the agent…")
    } catch (error) {
      room?.disconnect()
      if (!abandoned()) this.ended(`Couldn't start the call: ${error.message}`)
    }
  }

  hangUp() {
    this.endCall()
  }

  // Ends the call for the agent too: on its own, leaving the room looks like a caller coming back (a page reload), and
  // the agent holds the call open for them. keepalive lets the request finish when the page itself is going away.
  endCall() {
    if (this.hangUpUrl) {
      fetch(this.hangUpUrl, {
        method: "DELETE",
        keepalive: true,
        headers: { "X-CSRF-Token": document.getElementsByName("csrf-token")[0].content }
      }).catch(() => {})
      this.hangUpUrl = undefined
    }
    this.room?.disconnect()
  }

  // The browser held back the agent's audio until the user acts on the page (autoplay policy)
  enableAudio() {
    this.room?.startAudio()
  }

  async startCall() {
    const response = await fetch(this.urlValue, {
      method: "POST",
      headers: { Accept: "application/json", "X-CSRF-Token": document.getElementsByName("csrf-token")[0].content }
    })
    if (!response.ok) throw new Error(`the server responded ${response.status}`)
    return response.json()
  }

  buildRoom({ Room, RoomEvent, Track }) {
    const room = new Room()
    room
      .on(RoomEvent.TrackSubscribed, (track) => {
        if (track.kind !== Track.Kind.Audio) return
        this.audioTarget.appendChild(track.attach())
        this.setStatus("Connected — start talking")
      })
      .on(RoomEvent.TrackUnsubscribed, (track) => track.detach().forEach((element) => element.remove()))
      .on(RoomEvent.AudioPlaybackStatusChanged, () => {
        this.enableAudioTarget.classList.toggle("hidden", room.canPlaybackAudio)
      })
      .on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
        if (room.remoteParticipants.size === 0) return
        const agentSpeaking = speakers.some((participant) => participant !== room.localParticipant)
        this.setStatus(agentSpeaking ? "Agent speaking…" : "Listening…")
      })
      // The agent leaving is the agent hanging up
      .on(RoomEvent.ParticipantDisconnected, () => room.disconnect())
      .on(RoomEvent.Disconnected, () => {
        if (this.room === room) this.ended("Call ended")
      })
    return room
  }

  ended(status) {
    this.room = undefined
    this.hangUpUrl = undefined
    this.audioTarget.replaceChildren()
    this.hangUpTarget.classList.add("hidden")
    this.enableAudioTarget.classList.add("hidden")
    this.startTarget.classList.remove("hidden")
    this.startTarget.disabled = false
    this.setStatus(status)
  }

  setStatus(text) {
    this.statusTarget.textContent = text
  }
}
